import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const API_BASE = 'https://backend-gocq.onrender.com';

// ── JS-driven responsive hook — inline styles ko override karna padega CSS se nahi ──
const useWindowWidth = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};

const ImportantNews = () => {
  const navigate  = useNavigate();
  const width     = useWindowWidth();
  const isMobile  = width <= 480;
  const isTablet  = width <= 1024;

  const [loading,      setLoading]      = useState(false);
  const [mainNews,     setMainNews]     = useState(null);
  const [topNews,      setTopNews]      = useState([]);
  const [latestNews,   setLatestNews]   = useState([]);
  const [trendingNews, setTrendingNews] = useState([]);
  const [email,        setEmail]        = useState('');
  const [name,         setName]         = useState('');
  const [status,       setStatus]       = useState('idle');
  const [message,      setMessage]      = useState('');
  const [showForm,     setShowForm]     = useState(false);

  useEffect(() => { fetchAllNews(); }, []);

  const fetchAllNews = async () => {
    try {
      setLoading(true);
      const [mainRes, topRes, latestRes, trendingRes] = await Promise.all([
        fetch(`${API_BASE}/api/news/type/main?limit=1`),
        fetch(`${API_BASE}/api/news/type/top?limit=6`),
        fetch(`${API_BASE}/api/news/type/latest?limit=5`),
        fetch(`${API_BASE}/api/news/type/trending?limit=3`),
      ]);
      const [mainData, topData, latestData, trendingData] = await Promise.all([
        mainRes.json(), topRes.json(), latestRes.json(), trendingRes.json(),
      ]);
      if (mainData.success && mainData.data.length > 0) setMainNews(mainData.data[0]);
      if (topData.success)      setTopNews(topData.data);
      if (latestData.success)   setLatestNews(latestData.data);
      if (trendingData.success) setTrendingNews(trendingData.data);
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!showForm) { setShowForm(true); return; }
    if (!email.trim()) { setStatus('error'); setMessage('कृपया email दर्ज करें'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setStatus('error'); setMessage('कृपया valid email दर्ज करें'); return; }
    try {
      setStatus('loading'); setMessage('');
      const res  = await fetch(`${API_BASE}/api/subscribers/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() }),
      });
      const data = await res.json();
      if (res.status === 409)       { setStatus('duplicate'); setMessage(data.message || 'यह email पहले से सब्सक्राइब है'); return; }
      if (!res.ok || !data.success) { setStatus('error');     setMessage(data.message || 'कुछ गड़बड़ हुई, बाद में try करें'); return; }
      setStatus('success'); setMessage(data.message || 'सब्सक्रिप्शन सफल!');
      setEmail(''); setName('');
    } catch {
      setStatus('error'); setMessage('Network error, बाद में try करें');
    }
  };

  const statusColors = { success: '#16a34a', error: '#dc2626', duplicate: '#d97706', loading: '#6b7280' };

  const getTimeAgo = (date) => {
    const diff = Date.now() - new Date(date);
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 60) return `${m} मिनट पहले`;
    if (h < 24) return `${h} घंटे पहले`;
    if (d < 7)  return `${d} दिन पहले`;
    return new Date(date).toLocaleDateString('hi-IN');
  };

  const getImageUrl = (p) => {
    if (!p) return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
    if (p.startsWith('http')) return p;
    return `${API_BASE}${p}`;
  };

  const getYtId = (video) => {
    if (!video) return null;
    const m = video.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ||
              video.match(/[?&]v=([a-zA-Z0-9_-]{11})/) ||
              video.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
  };

  const formatViews = (v = 0) => {
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v;
  };

  const goToNews = (id) => { navigate(`/news/${id}`); window.scrollTo(0, 0); };

  const RED = '#f00400';

  // ── All layout values JS-driven (no CSS media queries for layout) ────────────
  const pad          = isMobile ? '16px 12px' : '28px 16px';
  const heroRatioPT  = isMobile ? '68%' : isTablet ? '60%' : '52%';
  const heroFontSize = isMobile ? 17 : isTablet ? 20 : 26;
  const titleFontSize = isMobile ? 16 : 20;
  const swiperSlides = isMobile ? 1 : 2;

  return (
    <div style={{ background: '#f8f8f8', fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tiro+Devanagari+Hindi:ital@0;1&family=Mukta:wght@300;400;600;700;800&display=swap');

        .imp-ratio     { position: relative; width: 100%; overflow: hidden; background: #111; }
        .imp-ratio img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.45s ease; }
        .imp-ratio-card { padding-top: 56.25%; }

        .imp-hero             { cursor: pointer; border-radius: 16px; overflow: hidden; border: 2px solid ${RED}; background: #000; box-shadow: 0 4px 24px rgba(240,4,0,0.12); transition: box-shadow 0.3s; margin-bottom: 28px; }
        .imp-hero:hover       { box-shadow: 0 8px 36px rgba(240,4,0,0.2); }
        .imp-hero:hover .imp-hero-img { transform: scale(1.04); }

        .imp-top-card             { cursor: pointer; border-radius: 12px; overflow: hidden; background: #fff; border: 1px solid #eee; box-shadow: 0 2px 10px rgba(0,0,0,0.06); transition: transform 0.25s, box-shadow 0.25s; display: flex; flex-direction: column; height: 100%; }
        .imp-top-card:hover       { transform: translateY(-4px); box-shadow: 0 12px 28px rgba(240,4,0,0.12); }
        .imp-top-card:hover .imp-ratio img { transform: scale(1.06); }
        .imp-top-card:hover .imp-top-ttl  { color: ${RED}; }

        .imp-latest                       { cursor: pointer; padding: 12px 0; border-bottom: 1px solid #f0f0f0; display: flex; gap: 12px; align-items: flex-start; }
        .imp-latest:last-child            { border-bottom: none; padding-bottom: 0; }
        .imp-latest:hover .imp-latest-ttl { color: ${RED}; }

        .imp-trend            { cursor: pointer; background: rgba(255,255,255,0.12); border-radius: 10px; padding: 14px; margin-bottom: 12px; display: flex; gap: 12px; align-items: flex-start; transition: background 0.2s; }
        .imp-trend:last-child { margin-bottom: 0; }
        .imp-trend:hover      { background: rgba(255,255,255,0.22); }

        .imp-vpill    { background: linear-gradient(135deg,#7c3aed,#5b21b6); color: #fff; font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 999px; display: inline-flex; align-items: center; gap: 3px; }
        .imp-gradient { background: linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 55%, transparent 100%); }
        .imp-play     { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 50px; height: 50px; background: rgba(255,255,255,0.18); backdrop-filter: blur(4px); border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid rgba(255,255,255,0.5); pointer-events: none; z-index: 3; }

        .lc2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .lc3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

        .imp-input            { width: 100%; padding: 10px 14px; border-radius: 10px; border: 1.5px solid #e5e5e5; font-size: 14px; font-family: inherit; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
        .imp-input:focus      { border-color: ${RED}; }
        .imp-subBtn           { width: 100%; color: #fff; padding: 12px; border-radius: 30px; font-weight: 700; font-size: 15px; font-family: inherit; border: none; cursor: pointer; background: ${RED}; transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s; }
        .imp-subBtn:hover:not(:disabled) { transform: scale(1.03); box-shadow: 0 6px 20px rgba(240,4,0,0.28); }
        .imp-subBtn:disabled  { opacity: 0.6; cursor: not-allowed; }

        .swiper-button-next, .swiper-button-prev { display: none !important; }
        .swiper-pagination-bullet-active { background: ${RED} !important; }
        .swiper-pagination-bullet         { background: #ccc; opacity: 1; }

        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: pad }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ width: 44, height: 44, border: '3px solid #fde', borderTop: `3px solid ${RED}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
            <p style={{ marginTop: 16, color: '#888' }}>समाचार लोड हो रहे हैं...</p>
          </div>
        ) : (
          /* ── outer layout: JS-driven flex direction ── */
          <div style={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', gap: isMobile ? 20 : 28, alignItems: 'flex-start' }}>

            {/* ══ Main Column ══ */}
            <div style={{ flex: '1 1 0', minWidth: 0, width: '100%' }}>

              {/* Hero */}
              {mainNews && (
                <div className="imp-hero" onClick={() => goToNews(mainNews._id)}>
                  {/* ratio box with JS-driven padding */}
                  <div style={{ position: 'relative', width: '100%', overflow: 'hidden', background: '#111', paddingTop: heroRatioPT }}>
                    <img
                      className="imp-hero-img"
                      src={getYtId(mainNews.video)
                        ? `https://img.youtube.com/vi/${getYtId(mainNews.video)}/hqdefault.jpg`
                        : getImageUrl(mainNews.image)}
                      alt={mainNews.title}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.45s ease' }}
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80'; }}
                    />
                    {mainNews.video && (
                      <div className="imp-play">
                        <svg width="20" height="20" fill="#fff" viewBox="0 0 20 20" style={{ marginLeft: 3 }}>
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                        </svg>
                      </div>
                    )}
                    <div className="imp-gradient" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
                    <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ background: RED, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 999 }}>{mainNews.category}</span>
                      {mainNews.video && <span className="imp-vpill">▶ VIDEO</span>}
                    </div>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: isMobile ? '14px 14px 18px' : '20px 20px 24px', zIndex: 2 }}>
                      <h1 className="lc3" style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: heroFontSize, fontWeight: 700, color: '#fff', margin: '0 0 8px', lineHeight: 1.38 }}>
                        {mainNews.title}
                      </h1>
                      {!isMobile && mainNews.description && (
                        <p className="lc2" style={{ fontSize: 14, color: 'rgba(255,255,255,0.82)', margin: '0 0 10px', lineHeight: 1.5 }}>
                          {mainNews.description}
                        </p>
                      )}
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>🕐 {getTimeAgo(mainNews.createdAt)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Top News Swiper */}
              <div>
                <h2 style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px', paddingLeft: 14, borderLeft: `4px solid ${RED}`, lineHeight: 1.3 }}>
                  मुख्य समाचार
                </h2>
                {topNews.length > 0 ? (
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    spaceBetween={16}
                    slidesPerView={swiperSlides}
                    loop={true}
                    autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                    pagination={{ clickable: true }}
                    style={{ paddingBottom: 40 }}
                  >
                    {topNews.map((news) => {
                      const ytId = getYtId(news.video);
                      return (
                        <SwiperSlide key={news._id} style={{ height: 'auto' }}>
                          <div className="imp-top-card" onClick={() => goToNews(news._id)}>
                            <div className="imp-ratio imp-ratio-card">
                              <img
                                src={ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : getImageUrl(news.image)}
                                alt={news.title}
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80'; }}
                              />
                              <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ background: RED, color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 999 }}>{news.category}</span>
                                {news.video && <span className="imp-vpill">▶ VIDEO</span>}
                              </div>
                              {ytId && (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                                  <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.88)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="13" height="13" fill="#7c3aed" viewBox="0 0 20 20" style={{ marginLeft: 2 }}>
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div style={{ padding: '14px 14px 16px', flex: 1 }}>
                              <p className="imp-top-ttl lc3" style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 15, fontWeight: 600, color: '#1a1a2e', margin: '0 0 8px', lineHeight: 1.5, transition: 'color 0.2s' }}>
                                {news.title}
                              </p>
                              <span style={{ fontSize: 12, color: '#bbb' }}>🕐 {getTimeAgo(news.createdAt)}</span>
                            </div>
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                ) : (
                  <p style={{ textAlign: 'center', color: '#bbb', padding: '24px 0' }}>कोई समाचार उपलब्ध नहीं</p>
                )}
              </div>
            </div>

            {/* ══ Sidebar — width JS-driven ══ */}
            <div style={{ width: isTablet ? '100%' : '340px', flexShrink: 0 }}>

              {/* Latest News */}
              <div style={{ background: '#fff', borderRadius: 16, border: `1.5px solid ${RED}`, boxShadow: '0 2px 14px rgba(0,0,0,0.06)', padding: '20px 18px', marginBottom: 24 }}>
                <h2 style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px', paddingLeft: 14, borderLeft: `4px solid ${RED}`, lineHeight: 1.3 }}>
                  ताज़ा खबरें
                </h2>
                {latestNews.length > 0 ? latestNews.map((news, i) => (
                  <div key={news._id} className="imp-latest" onClick={() => goToNews(news._id)}>
                    <span style={{ flexShrink: 0, width: 30, height: 30, background: RED, color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="imp-latest-ttl lc2" style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 14, fontWeight: 600, color: '#1a1a2e', margin: '0 0 5px', lineHeight: 1.45, transition: 'color 0.2s' }}>
                        {news.title}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#ccc' }}>🕐 {getTimeAgo(news.createdAt)}</span>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                          {news.video && <span className="imp-vpill">▶ VIDEO</span>}
                          <span style={{ fontSize: 10, background: '#f3f4f6', color: '#555', padding: '1px 8px', borderRadius: 999, fontWeight: 600 }}>{news.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )) : <p style={{ textAlign: 'center', color: '#bbb', padding: 16 }}>कोई खबर नहीं</p>}
              </div>

              {/* Trending */}
              <div style={{ background: RED, borderRadius: 16, padding: '20px 18px', marginBottom: 24 }}>
                <h2 style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: titleFontSize, fontWeight: 700, color: '#fff', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="20" height="20" fill="#fff" viewBox="0 0 20 20"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>
                  ट्रेंडिंग
                </h2>
                {trendingNews.length > 0 ? trendingNews.map((news, i) => (
                  <div key={news._id} className="imp-trend" onClick={() => goToNews(news._id)}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: 'rgba(255,255,255,0.4)', flexShrink: 0, lineHeight: 1 }}>#{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {news.video && (
                        <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 3, marginBottom: 5 }}>▶ VIDEO</span>
                      )}
                      <p className="lc2" style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 14, fontWeight: 600, color: '#fff', margin: '0 0 6px', lineHeight: 1.45 }}>
                        {news.title}
                      </p>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>👁 {formatViews(news.views)} views</span>
                    </div>
                  </div>
                )) : <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', padding: 16 }}>कोई ट्रेंडिंग न्यूज नहीं</p>}
              </div>

              {/* Subscribe */}
              <div style={{ background: '#fff', borderRadius: 16, border: `2px solid ${RED}`, boxShadow: '0 2px 14px rgba(0,0,0,0.06)', padding: '24px 18px', textAlign: 'center' }}>
                <div style={{ marginBottom: 12 }}>
                  {status === 'success'
                    ? <svg width="52" height="52" style={{ margin: '0 auto', display: 'block' }} fill="none" stroke={RED} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    : <svg width="52" height="52" style={{ margin: '0 auto', display: 'block' }} fill="none" stroke={RED} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
                  }
                </div>
                <h3 style={{ fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' }}>
                  {status === 'success' ? 'सब्सक्रिप्शन हो गया!' : 'न्यूज अलर्ट पाएं'}
                </h3>
                <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px', lineHeight: 1.55 }}>
                  {status === 'success' ? 'आपके inbox में confirmation email भेजा गया है।' : 'सबसे पहले खबरें पाने के लिए सब्सक्राइब करें'}
                </p>
                {showForm && status !== 'success' && (
                  <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                    <input className="imp-input" type="text" placeholder="आपका नाम (optional)" value={name} onChange={(e) => setName(e.target.value)} />
                    <input className="imp-input" type="email" placeholder="आपका email दर्ज करें *" value={email}
                      onChange={(e) => { setEmail(e.target.value); if (status !== 'idle') setStatus('idle'); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()} />
                  </div>
                )}
                {message && <p style={{ fontSize: 13, marginBottom: 10, fontWeight: 600, color: statusColors[status] || '#111' }}>{message}</p>}
                {status !== 'success' && (
                  <button className="imp-subBtn" onClick={handleSubscribe} disabled={status === 'loading'}>
                    {status === 'loading' ? 'सब्सक्राइब हो रहा है...' : showForm ? 'कन्फर्म करें ✓' : 'अभी सब्सक्राइब करें'}
                  </button>
                )}
                {showForm && status !== 'success' && (
                  <button onClick={() => { setShowForm(false); setStatus('idle'); setMessage(''); setEmail(''); setName(''); }}
                    style={{ marginTop: 8, fontSize: 12, color: '#bbb', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                    रद्द करें
                  </button>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportantNews;